import { useState, useEffect } from 'react';
import { X, ShieldCheck, Receipt, Download, FileText, CheckCircle2, AlertCircle, Banknote, ArrowRight, Wallet, DollarSign, Upload, Copy, Check, Sparkles } from 'lucide-react';
import { downloadInvoicePdf } from '../lib/invoicePdfGenerator';
import { getInvoiceSettingsFromFirestore, InvoiceSettings } from '../lib/invoiceSettings';
import { Order, DynamicPaymentGateway } from '../types';
import { saveManualPaymentSubmission, saveOrderToFirestore, getOrderByToken } from '../lib/ordersService';
import { fetchDynamicPaymentGatewaysFromFirestore } from '../lib/paymentGatewayService';
import { getInitialGateways } from '../lib/paymentGatewayService';


// Helper to get local date time string YYYY-MM-DDTHH:mm
const getLocalDatetimeNow = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const CustomerInvoiceView = ({ token, darkMode }: { token: string, darkMode: boolean }) => {
  const [invoice, setInvoice] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [gateways, setGateways] = useState<DynamicPaymentGateway[]>([]);
  const [selectedGateway, setSelectedGateway] = useState<DynamicPaymentGateway | null>(null);
  
  // Manual submission state
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualTrxId, setManualTrxId] = useState('');
  const [manualNote, setManualNote] = useState('');
  const [manualAmount, setManualAmount] = useState('0.00');
  const [manualDateTime, setManualDateTime] = useState(getLocalDatetimeNow());
  const [manualSenderAccount, setManualSenderAccount] = useState('');
  const [manualScreenshotUrl, setManualScreenshotUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [copiedText, setCopiedText] = useState(false);
  const [invoiceSettings, setInvoiceSettings] = useState<InvoiceSettings | null>(null);
  const [showQrZoom, setShowQrZoom] = useState(false);
  const handleDownloadPDF = () => { if(invoice) downloadInvoicePdf(invoice as any, invoiceSettings); };

  
  const handleScreenshotUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Screenshot file size exceeds 5MB limit.');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        // Compress image before setting to state to avoid Firestore 1MB document limit
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            const compressedBase64 = canvas.toDataURL('image/jpeg', 0.8);
            setManualScreenshotUrl(compressedBase64);
          }
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCopyNumber = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(true);
    setTimeout(() => setCopiedText(false), 2000);
  };

  useEffect(() => {
    // 1. Fetch invoice by token
    const fetchInvoice = async () => {
      try {
        const data = await getOrderByToken(token);
        
        if (data) {
          setInvoice(data);
        }
      } catch (err) {
        console.error('Error fetching invoice:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
    setGateways(getInitialGateways());
    fetchDynamicPaymentGatewaysFromFirestore().then(gws => { if (gws && gws.length > 0) setGateways(gws); });
    getInvoiceSettingsFromFirestore().then((settings) => {
      if (settings) setInvoiceSettings(settings);
    });
  }, [token]);

  if (loading) {
    return (
      <div className={`min-h-[80vh] flex items-center justify-center ${darkMode ? 'text-white' : 'text-slate-900'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className={`min-h-[80vh] flex flex-col items-center justify-center p-6 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
        <AlertCircle className="w-16 h-16 text-rose-500 mb-4" />
        <h2 className="text-2xl font-black mb-2">Invoice Not Found</h2>
        <p className="text-slate-400 text-center max-w-md">The secure invoice link is invalid or has expired.</p>
      </div>
    );
  }

  const isPaid = invoice.paymentStatus?.toLowerCase() === 'paid' || invoice.status?.toLowerCase() === 'paid';
  const isPendingVerification = invoice.paymentStatus?.toLowerCase() === 'pending verification' || invoice.paymentStatus?.toLowerCase() === 'pending';
  const currencySymbol = '$';

  const handleOnlinePay = async () => {
    if (!selectedGateway || !invoice) return;
    setIsSubmitting(true);
    try {
      const id = selectedGateway.id.toLowerCase();
      const apiEndpoint = id === 'nowpayments' ? '/api/nowpayments/create-invoice' : '/api/gateway/create-invoice';

      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gatewayId: selectedGateway.id,
          amount: invoice.total,
          currency: 'USD',
          orderId: invoice.id,
          customerEmail: invoice.customerEmail,
          customerName: invoice.customerName,
          items: invoice.items || []
        })
      });
      const data = await response.json();
      if (data.success && (data.checkout_url || data.invoice_url)) {
        window.location.href = data.checkout_url || data.invoice_url;
      } else {
        alert('Failed to generate payment session: ' + (data.message || 'Unknown error'));
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error(err);
      alert('Error connecting to payment gateway.');
      setIsSubmitting(false);
    }
  };

  
  const handleSubmitManual = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGateway) return;

    if (!manualTrxId.trim()) {
      alert('Transaction ID is required.');
      return;
    }

    setIsSubmitting(true);
    try {
      const submittedAmountNum = parseFloat(manualAmount) || invoice.total;

      const updatedOrder: Order = { 
        ...invoice, 
        paymentStatus: 'Pending Verification', 
        paymentMethod: selectedGateway.name,
        transactionId: manualTrxId.trim(),
        customerNotes: invoice.customerNotes 
            ? `${invoice.customerNotes}\n\nManual Payment (${selectedGateway.name}). Sender: ${manualSenderAccount.trim()}, TrxID: ${manualTrxId.trim()}`
            : `Manual Payment (${selectedGateway.name}). Sender: ${manualSenderAccount.trim()}, TrxID: ${manualTrxId.trim()}`,
        manualPaymentDetails: {
          transactionId: manualTrxId.trim(),
          paymentAmount: submittedAmountNum,
          paymentDateTime: manualDateTime || new Date().toISOString(),
          senderAccount: manualSenderAccount.trim(),
          screenshotUrl: manualScreenshotUrl,
          paymentNote: manualNote.trim(),
          submittedAt: new Date().toISOString()
        }
      };

      await saveOrderToFirestore(updatedOrder);
      await saveManualPaymentSubmission({
        id: invoice.id,
        orderId: invoice.id,
        customerName: invoice.customerName || '',
        customerEmail: invoice.customerEmail || '',
        gatewayName: selectedGateway.name || 'Manual Payment',
        transactionId: manualTrxId.trim(),
        senderAccount: manualSenderAccount.trim(),
        amount: submittedAmountNum,
        status: 'pending',
        createdAt: new Date().toISOString(),
        receiptImage: manualScreenshotUrl || '',
        productDetails: '',
        note: manualNote || '',
        rejectionReason: ''
      });
      
      // Also notify Admin via email, like CheckoutModal does (optional but good practice)
      try {
        await fetch('/api/auth/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: 'support@rheditography.net',
            subject: 'New Manual Payment Submission',
            html: `
              <div style="font-family: Arial, sans-serif; background: #0f172a; color: #ffffff; padding: 30px; border-radius: 16px;">
                <h2 style="color: #fbbf24; margin-top: 0;">🚨 New Manual Payment Received</h2>
                <p>A new manual payment submission requires administrative verification:</p>
                <div style="background: #1e293b; padding: 20px; border-radius: 12px; margin: 20px 0; border-left: 4px solid #f59e0b;">
                  <p style="margin: 6px 0;"><strong>Order ID:</strong> #${updatedOrder.id}</p>
                  <p style="margin: 6px 0;"><strong>Customer Email:</strong> ${updatedOrder.customerEmail}</p>
                  <p style="margin: 6px 0;"><strong>Gateway:</strong> ${selectedGateway.name}</p>
                  <p style="margin: 6px 0;"><strong>Amount:</strong> ${submittedAmountNum}</p>
                  <p style="margin: 6px 0;"><strong>Transaction ID:</strong> ${manualTrxId.trim()}</p>
                  <p style="margin: 6px 0;"><strong>Sender Account:</strong> ${manualSenderAccount.trim()}</p>
                </div>
                <p>Log in to your Admin Dashboard under <strong>Manual Verification</strong> to approve or reject this submission.</p>
              </div>
            `
          })
        });
      } catch (err) {
        console.warn('Failed to send admin email notification:', err);
      }

      setSubmitSuccess(true);
      setInvoice(updatedOrder);
    } catch (err) {
      console.error(err);
      alert('An error occurred while submitting payment.');
    } finally {
      setIsSubmitting(false);
    }
  };
      

  return (
    
    <div className={`min-h-screen pt-6 sm:pt-12 pb-6 sm:pb-12 px-2 sm:px-4 flex items-center justify-center ${darkMode ? 'bg-slate-950 text-slate-100' : 'bg-slate-50 text-slate-900'}`}>
      {showQrZoom && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-sm cursor-zoom-out animate-fade-in"
          onClick={() => setShowQrZoom(false)}
        >
          <div className="relative max-w-2xl w-full flex flex-col items-center">
            <button 
              className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
              onClick={(e) => { e.stopPropagation(); setShowQrZoom(false); }}
            >
              <X className="w-8 h-8" />
            </button>
            <div className="p-4 bg-white rounded-3xl shadow-2xl animate-scale-up">
              <img
                src={selectedGateway?.qrCodeUrl || invoiceSettings?.cryptoDetails?.qrCodeUrl || `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(selectedGateway?.cryptoWallet || selectedGateway?.merchantAccountNumber || invoiceSettings?.cryptoDetails?.walletAddress || '')}`}
                alt="Enlarged QR Code"
                className="w-full max-w-[500px] h-auto object-contain rounded-xl"
              />
            </div>
            <p className="text-white mt-6 font-bold tracking-wider text-sm">Scan to complete payment</p>
          </div>
        </div>
      )}

      
      {/* Outer Glowing Wrapper */}
      <div className={`relative w-full max-w-5xl p-[1.5px] rounded-[24px] shadow-[0_0_40px_rgba(139,92,246,0.15)] flex flex-col shrink-0 transition-colors animate-fade-in ${darkMode ? 'bg-gradient-to-br from-[#0070f3] via-[#8b5cf6] to-[#06b6d4]' : 'bg-slate-300'}`}>
        {/* Inner Card Wrapper with fixed max-height and scrolling */}
        <div className={`relative w-full flex flex-col rounded-[24px] shadow-2xl overflow-hidden ${darkMode ? 'bg-[#0a0f1c] text-white' : 'bg-white text-slate-900'}`}>
          {/* Optional: The tiny gradient top bar from checkout modal */}
          {darkMode && <div className="absolute top-0 left-8 w-20 h-[3px] bg-gradient-to-r from-blue-500 to-purple-500 rounded-b-full z-10"></div>}
          {/* Scrollable Content Container */}
          <div className="flex-1 overflow-y-auto max-h-[85vh] p-4 sm:p-6 lg:p-8 space-y-6 custom-scrollbar">

        
        {/* INVOICE HEADER (Matches Checkout top header) */}
        <div className={`flex-shrink-0 flex flex-col sm:flex-row items-start sm:items-center justify-between px-6 py-5 rounded-[24px] border shadow-xl ${darkMode ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 shadow-lg ${darkMode ? 'bg-slate-950 border border-white/10' : 'bg-slate-100 border border-slate-200'}`}>
              <Receipt className="w-7 h-7 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight">Invoice {invoice.invoiceNumber || invoice.id}</h1>
              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'} mt-1 font-medium`}>
                Issued to: <strong className={darkMode ? 'text-white' : 'text-slate-900'}>{invoice.customerName}</strong> ({invoice.customerEmail})
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 mt-5 sm:mt-0 ml-auto sm:ml-0">
            <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 ${
              isPaid ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20' : 
              isPendingVerification ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20' : 
              'bg-rose-500/10 text-rose-500 border border-rose-500/20'
            }`}>
              {isPaid && <CheckCircle2 className="w-3 h-3" />}
              {isPendingVerification && <AlertCircle className="w-3 h-3" />}
              {!isPaid && !isPendingVerification && <AlertCircle className="w-3 h-3" />}
              {isPaid ? 'PAID' : isPendingVerification ? 'PENDING VERIFICATION' : 'UNPAID'}
            </div>
            <div className="text-3xl font-black text-cyan-400">
              {currencySymbol}{Number(invoice.total).toFixed(2)}
            </div>
          </div>
        </div>

        {/* TWO COLUMN MAIN CONTENT */}
        <div className="grid lg:grid-cols-12 gap-6 max-w-full">
          
          {/* LEFT COLUMN: SECURE PAYMENT (Matches Checkout Gateways & Forms) */}
          <div className="lg:col-span-7 space-y-6 max-w-full min-w-0">
            
            {isPaid ? (
              <div className={`p-8 rounded-[24px] border text-center space-y-4 ${darkMode ? 'bg-slate-900 border-emerald-500/30' : 'bg-white border-emerald-500/30'} shadow-xl shadow-emerald-500/10`}>
                <div className="w-24 h-24 rounded-full bg-emerald-500/20 text-emerald-500 border border-emerald-500/30 mx-auto flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/20">
                  <CheckCircle2 className="w-12 h-12" />
                </div>
                <h3 className="text-3xl font-black text-emerald-400 tracking-tight">Payment Complete</h3>
                <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'} max-w-md mx-auto leading-relaxed`}>
                  Thank you for your business. Your payment has been successfully processed and verified.
                </p>
              </div>
            ) : isPendingVerification ? (
              <div className={`p-8 rounded-[24px] border text-center space-y-4 ${darkMode ? 'bg-slate-900 border-amber-500/30' : 'bg-white border-amber-500/30'} shadow-xl shadow-amber-500/10`}>
                <div className="w-24 h-24 rounded-full bg-amber-500/20 text-amber-500 border border-amber-500/30 mx-auto flex items-center justify-center mb-6 shadow-lg shadow-amber-500/20 animate-pulse">
                  <AlertCircle className="w-12 h-12" />
                </div>
                <h3 className="text-3xl font-black text-amber-400 tracking-tight">Payment Pending Verification</h3>
                <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'} max-w-md mx-auto leading-relaxed`}>
                  We are currently reviewing your manual payment submission. This usually takes 1-12 hours.
                </p>
              </div>
            ) : (
              <div className={`p-6 rounded-3xl border ${darkMode ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'} shadow-xl max-w-full min-w-0`}>
                 <div className="flex items-center gap-2 mb-4">
                   <ShieldCheck className="w-5 h-5 text-emerald-400" />
                   <h3 className="text-lg font-black uppercase tracking-wider">Secure Payment</h3>
                 </div>
                 
                 {/* GATEWAY SELECTION GRID (Matches Checkout) */}
                 <div>
                   <label className={`block text-xs font-extrabold uppercase tracking-wider mb-2.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                     Select Payment Gateway
                   </label>
                   <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[350px] overflow-y-auto custom-scrollbar pr-1">
                     {gateways.filter(g => {
                       if (!g.enabled) return false;
                       const method = invoice?.paymentMethod || '';
                       if (method === 'Direct Bank Wire') {
                         return g.name.toLowerCase().includes('bank') || g.name.toLowerCase().includes('wire') || g.type === 'manual';
                       }
                       if (method === 'Crypto (USDT / BTC)') {
                         return g.name.toLowerCase().includes('crypto') || g.name.toLowerCase().includes('bitcoin') || g.name.toLowerCase().includes('usdt') || g.type === ('crypto' as any) || g.id === 'binance' || g.id === 'bybit' || g.id === 'coinbase' || g.id === 'nowpayments';
                       }
                       return true;
                     }).map(gateway => {
                       const isSelected = selectedGateway?.id === gateway.id;
                       const isCryptoGW = gateway.type === ('crypto' as any) || gateway.name.toLowerCase().includes('crypto') || gateway.name.toLowerCase().includes('usdt');
                       return (
                         <button
                            key={gateway.id}
                            type="button"
                            onClick={() => { setSelectedGateway(gateway); setShowManualForm(gateway.type === 'manual' || gateway.id === 'manual'); }}
                            className={`w-full p-3.5 rounded-2xl border text-left transition-all group flex flex-col justify-between min-h-[90px] relative overflow-hidden ${
                              isSelected 
                                ? 'bg-gradient-to-br from-blue-900/70 via-slate-900 to-cyan-950/90 border-cyan-400 text-white shadow-xl shadow-cyan-500/20 ring-1 ring-cyan-400/50'
                                : darkMode ? 'bg-slate-900/95 hover:bg-slate-900 border-slate-800 text-slate-300' : 'bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-600'
                            }`}
                         >
                           {isSelected && (
                              <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-cyan-400 flex items-center justify-center">
                                <Check className="w-3 h-3 text-slate-950 stroke-[3]" />
                              </div>
                           )}
                           <div className="flex items-center gap-3">
                             <div className={`w-10 h-10 rounded-2xl border p-2 flex items-center justify-center shrink-0 shadow-lg ${
                                isSelected ? 'bg-cyan-500/20 border-cyan-400 text-cyan-300' : darkMode ? 'bg-slate-950 border-slate-700 text-slate-400' : 'bg-white border-slate-200 text-slate-500'
                             }`}>
                               {gateway.type === 'manual' || gateway.id === 'manual' ? <Banknote className="w-5 h-5 text-emerald-400" /> : <Wallet className="w-5 h-5 text-blue-400" />}
                             </div>
                             <div className="pr-6">
                               <div className={`font-black text-xs leading-tight ${isSelected ? 'text-white' : darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                                 {gateway.name}
                               </div>
                               <div className={`text-[10px] font-bold mt-1 inline-block px-2 py-0.5 rounded-full ${
                                 isSelected ? 'bg-cyan-950 text-cyan-400 border border-cyan-800' : darkMode ? 'bg-slate-800 text-slate-400 border border-slate-700' : 'bg-slate-200 text-slate-600 border border-slate-300'
                               }`}>
                                 {gateway.type === 'manual' || gateway.id === 'manual' ? 'Manual Verification' : isCryptoGW ? 'Crypto (150+)' : 'Instant Processing'}
                               </div>
                             </div>
                           </div>
                         </button>
                       );
                     })}
                   </div>

                   {selectedGateway && !showManualForm && (
                     <div className="mt-6">
                       <button onClick={handleOnlinePay} className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-500 font-extrabold text-white text-xs shadow-xl hover:scale-[1.02] transition-transform cursor-pointer flex items-center justify-center gap-2">
                         Pay {currencySymbol}{invoice.total.toFixed(2)} securely <ArrowRight className="w-4 h-4" />
                       </button>
                     </div>
                   )}

{selectedGateway && showManualForm && (() => {
                  const effectiveAddress = selectedGateway.cryptoWallet || selectedGateway.merchantAccountNumber || invoiceSettings?.cryptoDetails?.walletAddress || '0x71C7656EC7ab88b098defB751B7401B5f6d8976F';
                  const effectiveQrCode = selectedGateway.qrCodeUrl || invoiceSettings?.cryptoDetails?.qrCodeUrl || `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(effectiveAddress)}`;
                  const effectiveNetwork = selectedGateway.cryptoNetwork || invoiceSettings?.cryptoDetails?.network || 'USDT / BTC / ETH';
                  const isCrypto = selectedGateway.type === ('crypto' as any) || selectedGateway.name.toLowerCase().includes('crypto') || selectedGateway.name.toLowerCase().includes('usdt');
                  return (
                                                      <form onSubmit={handleSubmitManual} className="mt-4 space-y-4 max-w-full">
                    
                    {/* HIGH VISIBILITY CRYPTO BARCODE & QR CODE BANNER */}
                    <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-b from-slate-900 via-slate-900 to-purple-950/40 border border-purple-500/40 shadow-xl space-y-4 text-center max-w-full overflow-hidden">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-white/10 pb-2.5 text-left gap-2">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded-lg bg-purple-500/20 text-purple-400 border border-purple-500/30 shrink-0">
                            <Wallet className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <span className="font-extrabold text-xs text-white block truncate">
                              Official {isCrypto ? 'Crypto Wallet & Barcode' : 'Bank Account Details'}
                            </span>
                            <span className="text-[10px] text-purple-300 font-medium block truncate">
                              {isCrypto ? 'Scan QR Code or Copy Wallet Address' : 'Copy Account Details Below'}
                            </span>
                          </div>
                        </div>
                        <span className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full bg-purple-900/60 text-purple-200 border border-purple-600/50 self-start sm:self-auto max-w-full truncate">
                          {effectiveNetwork}
                        </span>
                      </div>

                      {isCrypto && (
                        <div className="flex flex-col items-center justify-center py-2 space-y-2">
                          {/* DISPLAYED BARCODE / QR CODE IMAGE */}
                          <div 
                            onClick={() => setShowQrZoom(true)}
                            className="group relative p-2 bg-white rounded-[1.25rem] border border-purple-400/30 shadow-xl shadow-purple-500/10 hover:scale-[1.02] active:scale-95 transition-all cursor-pointer inline-block max-w-full"
                            title="Click to Enlarge / Zoom QR Code"
                          >
                            <img
                              src={effectiveQrCode}
                              alt="Crypto Wallet Barcode QR Code"
                              className="w-32 h-32 sm:w-40 sm:h-40 object-contain rounded-xl max-w-full"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(effectiveAddress)}`;
                              }}
                            />
                            <div className="absolute inset-0 bg-slate-950/40 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5 text-white text-xs font-bold">
                              <Sparkles className="w-4 h-4 text-purple-300" />
                              <span>Tap to Zoom</span>
                            </div>
                          </div>
                          <p className="text-[11px] text-slate-400 font-medium">
                            Scan barcode with Binance, Trust Wallet, Metamask, or Crypto App
                          </p>
                        </div>
                      )}

                      {/* COPYABLE WALLET ADDRESS */}
                      <div className="p-3 sm:p-3.5 rounded-xl bg-slate-950/95 border border-purple-500/30 space-y-1.5 text-left max-w-full overflow-hidden">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-[10px] font-bold text-purple-300 uppercase tracking-wider">
                            {isCrypto ? `Wallet Address (${effectiveNetwork}):` : 'Account Details:'}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleCopyNumber(effectiveAddress)}
                            className="px-2.5 py-1 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-[10px] font-bold transition-all flex items-center gap-1 cursor-pointer shadow-md shrink-0 self-start sm:self-auto"
                          >
                            {copiedText ? (
                              <>
                                <Check className="w-3 h-3 text-emerald-300 shrink-0" />
                                <span className="whitespace-nowrap">Copied!</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3 shrink-0" />
                                <span className="whitespace-nowrap">{isCrypto ? 'Copy Address' : 'Copy Number'}</span>
                              </>
                            )}
                          </button>
                        </div>
                        <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800 font-mono text-xs font-extrabold text-cyan-300 break-all select-all max-w-full">
                          {effectiveAddress}
                        </div>
                      </div>

                      {/* MANUAL DETAILS / INSTRUCTIONS */}
                      <div className="p-3 sm:p-3.5 rounded-xl bg-slate-900/95 border border-white/5 space-y-2 text-left max-w-full">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          {selectedGateway.merchantName && (
                            <div>
                              <span className="text-[10px] text-slate-400 block font-medium">Account / Merchant Name</span>
                              <span className="font-bold text-white break-words">{selectedGateway.merchantName}</span>
                            </div>
                          )}
                          {selectedGateway.bankName && (
                            <div>
                              <span className="text-[10px] text-slate-400 block font-medium">Bank / Vault</span>
                              <span className="font-bold text-white break-words">{selectedGateway.bankName}</span>
                            </div>
                          )}
                        </div>
                        {selectedGateway.instructions && (
                          <div className="pt-2 border-t border-white/5">
                            <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider block mb-0.5">
                              Instructions:
                            </span>
                            <p className="text-xs text-slate-300 whitespace-pre-line leading-relaxed font-medium break-words">
                              {selectedGateway.instructions}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* MANUAL PAYMENT SUBMISSION FORM */}
                    <div className="p-3.5 sm:p-4 rounded-2xl bg-slate-900/95 border border-slate-800 space-y-3 text-left max-w-full">
                      <h4 className="font-extrabold text-xs text-white uppercase tracking-wider flex items-center gap-1.5">
                        <FileText className="w-4 h-4 text-cyan-400 shrink-0" />
                        <span>Payment Confirmation Details</span>
                      </h4>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[11px] font-bold text-slate-300 mb-1">Transaction ID (TrxID) *</label>
                          <input
                            type="text"
                            required
                            value={manualTrxId}
                            onChange={(e) => setManualTrxId(e.target.value)}
                            placeholder="e.g. 9J82A10K9"
                            className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-xs font-mono font-bold text-cyan-300 focus:outline-none focus:border-cyan-400"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-bold text-slate-300 mb-1">Payment Amount *</label>
                          <input
                            type="number"
                            step="0.01"
                            required
                            value={manualAmount}
                            onChange={(e) => setManualAmount(e.target.value)}
                            className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-xs font-bold text-white focus:outline-none focus:border-cyan-400"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="block text-[11px] font-bold text-slate-300">Payment Date & Time *</label>
                            <button
                              type="button"
                              onClick={() => setManualDateTime(getLocalDatetimeNow())}
                              className="text-[10px] text-cyan-400 hover:text-cyan-300 font-bold hover:underline cursor-pointer"
                            >
                              Set to Now
                            </button>
                          </div>
                          <input
                            type="datetime-local"
                            required
                            value={manualDateTime}
                            onChange={(e) => setManualDateTime(e.target.value)}
                            style={{ colorScheme: 'dark' }}
                            className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-xs font-semibold text-white focus:outline-none focus:border-cyan-400 max-w-full cursor-pointer"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-bold text-slate-300 mb-1">Sender Number / Wallet Account *</label>
                          <input
                            type="text"
                            required
                            value={manualSenderAccount}
                            onChange={(e) => setManualSenderAccount(e.target.value)}
                            placeholder="e.g. 0x... / Wallet Address"
                            className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-xs font-bold text-white focus:outline-none focus:border-cyan-400"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-slate-300 mb-1">
                          Payment Screenshot / Receipt (Optional)
                        </label>
                        <div className="flex items-center gap-3">
                          <label className="flex-1 px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 hover:border-cyan-400 text-xs text-slate-300 font-bold cursor-pointer flex items-center gap-2 transition-colors min-w-0">
                            <Upload className="w-4 h-4 text-cyan-400 shrink-0" />
                            <span className="truncate">{manualScreenshotUrl ? 'Change Screenshot' : 'Upload Receipt Screenshot'}</span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleScreenshotUpload}
                              className="hidden"
                            />
                          </label>
                          {manualScreenshotUrl && (
                            <div className="relative group shrink-0">
                              <img
                                src={manualScreenshotUrl}
                                alt="Screenshot Preview"
                                className="w-10 h-10 rounded-lg object-cover border border-cyan-400"
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-slate-300 mb-1">Payment Note (Optional)</label>
                        <input
                          type="text"
                          value={manualNote}
                          onChange={(e) => setManualNote(e.target.value)}
                          placeholder="Additional details..."
                          className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-xs font-bold text-white focus:outline-none focus:border-cyan-400"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-3.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:scale-[1.02] text-white font-black uppercase tracking-wider text-xs transition-all shadow-lg shadow-cyan-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span>Submitting...</span>
                          </>
                        ) : (
                          <>
                            <span>Submit Payment Proof</span>
                            <Upload className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                  );
                })()}

                 </div>
              </div>
            )}
          </div>

          {/* RIGHT COLUMN: INVOICE DETAILS (Matches Checkout Sticky Order Summary) */}
          <div className={`lg:col-span-5 flex flex-col justify-between space-y-6 p-6 rounded-[24px] border lg:sticky lg:top-24 self-start ${darkMode ? 'bg-slate-900/60 border-white/10' : 'bg-white border-slate-200 shadow-xl'}`}>
             <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <h4 className={`font-extrabold text-sm uppercase tracking-wider flex items-center gap-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    <FileText className="w-4 h-4 text-cyan-500" />
                    Invoice Summary
                  </h4>
                  <span className="px-2.5 py-1 rounded-lg bg-cyan-500/20 text-cyan-400 text-[10px] font-bold border border-cyan-500/30">
                    {invoice.items?.length || 0} Item{invoice.items?.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="max-h-[300px] overflow-y-auto custom-scrollbar pr-2 space-y-3">
                  {invoice.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-start gap-4">
                      <div className="min-w-0 flex-1">
                        <p className={`text-xs font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>{item.product?.title || 'Custom Item'}</p>
                        <p className={`text-[10px] ${darkMode ? 'text-slate-400' : 'text-slate-500'} mt-0.5`}>
                          Format: {item.product?.formats?.[0] || 'Digital'} • Qty: {item.quantity || 1}
                        </p>
                      </div>
                      <p className={`text-xs font-black shrink-0 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                        {currencySymbol}{Number((item.product?.price || 0) * (item.quantity || 1)).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
                
                <div className="pt-4 border-t border-white/10 space-y-2.5">
                  <div className={`flex justify-between items-center text-xs font-bold ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    <span>Subtotal</span>
                    <span className={darkMode ? 'text-white' : 'text-slate-900'}>{currencySymbol}{(invoice.subtotal || invoice.total).toFixed(2)}</span>
                  </div>
                  {invoice.taxAmount > 0 && (
                    <div className={`flex justify-between items-center text-xs font-bold ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                      <span>Tax / Processing Fee</span>
                      <span className={darkMode ? 'text-white' : 'text-slate-900'}>{currencySymbol}{invoice.taxAmount.toFixed(2)}</span>
                    </div>
                  )}
                </div>
                
                <div className={`pt-4 border-t border-white/10 flex justify-between items-center ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                   <span className="font-black text-sm uppercase tracking-wider">Total Amount</span>
                   <span className="font-black text-2xl text-cyan-400">{currencySymbol}{invoice.total.toFixed(2)}</span>
                </div>
             </div>
             
             <div className="pt-6 mt-6 border-t border-white/10">
                <button onClick={handleDownloadPDF} className="w-full py-3.5 rounded-2xl bg-slate-800 hover:bg-slate-700 text-white font-extrabold text-xs flex items-center justify-center gap-2 transition-transform hover:scale-[1.02] shadow-xl border border-slate-700">
                   <Download className="w-4 h-4" />
                   Download PDF Invoice
                </button>
             </div>
          </div>
          
        </div>
              </div>
      </div>
    </div>
    </div>
  );
};
